import { addDays, addHours, startOfDay } from 'date-fns';
import { InputAddress } from '../../../types/address';
import { Contract, ManageContractsPayment, OmaLummeAddOnTemplate, OmaLummeCampaign } from '../../../types/contract';
import lummectx from '../../../LummeContext';
import { Ledger } from '../../../types/billing';
import { SimpleUser } from '../../../types/customer';

export type AppState = {
  ssn: string;
  filteredContracts: Contract[];
  filteredLedgers: Ledger[];
  selectedForm: number;
  selectedForm2: number;
  selectedForm4: number;
  formPage: number;
  oldContract: Contract | undefined;
  oldContractOwner?: SimpleUser | undefined;
  endDate: Date | undefined;
  startDate: Date | undefined;
  createNewContract: boolean | undefined;
  newAddress: InputAddress | undefined;
  primaryAddress?: InputAddress | undefined;
  terminateLedgerAddress?: InputAddress | undefined;
  newAddressUseCurrentAddress: boolean;
  terminateLedgerUseCurrentAddress: boolean;
  changeAddressDate?: Date | undefined;
  houseType: string;
  changePrimaryAddress: boolean | undefined;
  campaignOmaLummePrices: OmaLummeCampaign[];
  newContract: string;
  addOnTemplates: OmaLummeAddOnTemplate[];
  extraServices: number[];
  useVaihtoturva: string | undefined;
  payment: ManageContractsPayment;
  multipleOwners: boolean;
  multipleOwnersNewAddress: InputAddress | undefined;
};

type AppAction =
  | ['changeSSN', string]
  | ['filterContracts', Contract[]]
  | ['filterLedgers', Ledger[]]
  | ['changeForm', number]
  | ['changeForm2', number]
  | ['changeForm4', number]
  | ['changePage', number]
  | ['changeOldContract', Contract | undefined]
  | ['changeOldContractOwner', SimpleUser | undefined]
  | ['changeEnd', Date | undefined]
  | ['changeStart', Date | undefined]
  | ['newContract', boolean | undefined]
  | ['newAddress', InputAddress | undefined]
  | ['newPrimaryAddress', InputAddress | undefined]
  | ['terminateLedgerAddress', InputAddress | undefined]
  | ['newAddressUseCurrentAddress', boolean]
  | ['terminateLedgerUseCurrentAddress', boolean]
  | ['newAddressDate', Date | undefined]
  | ['changeHouse', string]
  | ['primaryAddressChange', boolean | undefined]
  | ['changeCampaigns', OmaLummeCampaign[]]
  | ['changeNewContract', string]
  | ['changeTemplates', OmaLummeAddOnTemplate[]]
  | ['changeServices', number[]]
  | ['changePayment', ManageContractsPayment]
  | ['changeUseVaihtoturva', string | undefined]
  | ['changeMultipleOwners', boolean]
  | ['multipleOwnersNewAddress', InputAddress | undefined];

function appStateReducer(state: AppState, action: AppAction) {
  switch (action[0]) {
    case 'changeSSN':
      return { ...state, ssn: action[1] };
    case 'filterContracts':
      return { ...state, filteredContracts: action[1] };
    case 'filterLedgers':
      return { ...state, filteredLedgers: action[1] };
    case 'changeForm':
      return {
        ...state,
        selectedForm: action[1],
        selectedForm2: 0,
        selectedForm4: 0,
        oldContract: undefined,
        oldContractOwner: undefined,
        createNewContract: undefined,
        houseType: '',
        newContract: '',
        extraServices: [],
        useVaihtoturva: '',
      };
    case 'changeForm2':
      return {
        ...state,
        selectedForm2: action[1],
        selectedForm4: 0,
        oldContract: undefined,
        oldContractOwner: undefined,
        createNewContract: undefined,
        houseType: '',
        newContract: '',
        extraServices: [],
        useVaihtoturva: '',
      };
    case 'changeForm4':
      return {
        ...state,
        selectedForm4: action[1],
        selectedForm2: 0,
        oldContract: undefined,
        oldContractOwner: undefined,
        createNewContract: undefined,
        houseType: '',
        newContract: '',
        extraServices: [],
        useVaihtoturva: '',
      };
    case 'changePage':
      return { ...state, formPage: action[1] };
    case 'changeOldContract':
      return { ...state, oldContract: action[1] };
    case 'changeOldContractOwner':
      return { ...state, oldContractOwner: action[1] };
    case 'changeEnd':
      return { ...state, endDate: action[1] };
    case 'changeStart':
      return { ...state, startDate: action[1] };
    case 'newContract':
      return { ...state, createNewContract: action[1] };
    case 'newAddress':
      return { ...state, newAddress: action[1] };
    case 'newPrimaryAddress':
      return { ...state, primaryAddress: action[1] };
    case 'terminateLedgerAddress':
      return { ...state, terminateLedgerAddress: action[1] };
    case 'newAddressUseCurrentAddress':
      return { ...state, newAddressUseCurrentAddress: action[1] };
    case 'terminateLedgerUseCurrentAddress':
      return { ...state, terminateLedgerUseCurrentAddress: action[1] };
    case 'newAddressDate':
      return { ...state, changeAddressDate: action[1] };
    case 'changeHouse':
      return { ...state, houseType: action[1], extraServices: [] };
    case 'primaryAddressChange':
      return { ...state, changePrimaryAddress: action[1] };
    case 'changeCampaigns':
      return { ...state, campaignOmaLummePrices: action[1] };
    case 'changeNewContract':
      return { ...state, newContract: action[1], extraServices: [] };
    case 'changeTemplates':
      return { ...state, addOnTemplates: action[1] };
    case 'changeServices':
      return { ...state, extraServices: action[1] };
    case 'changeUseVaihtoturva':
      return { ...state, useVaihtoturva: action[1] };
    case 'changePayment':
      return { ...state, payment: action[1] };
    case 'changeMultipleOwners':
      return { ...state, multipleOwners: action[1] };
    case 'multipleOwnersNewAddress':
      return { ...state, multipleOwnersNewAddress: action[1] };
  }
}

const defaultDate = addDays(addHours(startOfDay(new Date()), 12), 5);

const ManageContractsApp = lummectx(appStateReducer, () => ({
  ssn: '',
  activeChild: undefined,
  filteredContracts: [],
  filteredLedgers: [],
  selectedForm: 0,
  selectedForm2: 0,
  selectedForm4: 0,
  formPage: 0,
  oldContract: undefined,
  oldContractOwner: undefined,
  endDate: defaultDate,
  startDate: defaultDate,
  createNewContract: undefined,
  newAddress: undefined,
  primaryAddress: undefined,
  terminateLedgerAddress: undefined,
  newAddressUseCurrentAddress: false,
  terminateLedgerUseCurrentAddress: false,
  changeAddressDate: defaultDate,
  houseType: '',
  changePrimaryAddress: undefined,
  campaignOmaLummePrices: [],
  newContract: '',
  addOnTemplates: [],
  extraServices: [],
  useVaihtoturva: undefined,
  payment: {
    paymentType: '',
    paymentTypeEbillBank: '',
    paymentTypeEbillValid: true,
    paymentTypeNewEmail: '',
    paymentTypeNewEmailValid: true,
    paymentTypeNewAddress: undefined,
  },
  multipleOwners: false,
  multipleOwnersNewAddress: undefined,
}));

const ManageContractsProvider = ManageContractsApp.Provider;

export { ManageContractsApp, ManageContractsProvider };
